import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import SelectApTemplate from '../../../components/Contact/Communicator/SelectApTemplate/SelectApTemplate';
import ButtonLoading from '../../../components/Shared/ButtonLoading';
import useAsyncCreateApTemplate from '../../../hooks/useAsyncCreateApTemplate';
import useAsyncGetApTemplateById from '../../../hooks/useAsyncGetApTemplateById';
import { DEFAULT_COMMON_FORM_NUMBER } from '../../../sic-lib/defaults';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { buildCreateApTemplate, buildGetApTemplateById } from '../../../sic-lib/types/ApTemplate';
import { IErrorFormValidation } from '../../../sic-lib/utils/formValidationHelpers';
import { setTemplate } from '../../../store/communicator';
import { useAppDispatch } from '../../../store/hooks';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(7, 1, 0),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(7, 2, 0),
        },
    },
    textWrapper: {
        margin: theme.spacing(3.5, 0),
    },
    selectWrapper: {
        margin: theme.spacing(3, 0),
    },
}));

interface ComponentState {
    templateId: number;
    formError: IErrorFormValidation | null;
}

export default function ApTemplateSelect() {
    const dispatch = useAppDispatch();
    const { classes } = useStyles();

    /* Create plan logic */

    const { requestStatus: apCreateRequestStatus, asyncCreateApTemplate } =
        useAsyncCreateApTemplate();

    const handleCreate = async () => {
        const templateConfig = buildCreateApTemplate();

        const asyncResult = await asyncCreateApTemplate(templateConfig);

        if (asyncResult) {
            dispatch(setTemplate(asyncResult));
        }
    };

    /* Select plan logic */

    const { requestStatus: apSelectRequestStatus, asyncGetApTemplateById } =
        useAsyncGetApTemplateById();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            templateId: DEFAULT_COMMON_FORM_NUMBER,
            formError: null,
        },
    );

    const handleSelect = async () => {
        try {
            const requestObj = buildGetApTemplateById({
                templateId: compState.templateId,
            });

            const asyncResult = await asyncGetApTemplateById(requestObj);

            if (asyncResult) {
                dispatch(setTemplate(asyncResult));
            }
        } catch (error: any) {
            if (error.isFormError) {
                compDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <div className={classes.root}>
            <div className={classes.textWrapper}>
                <Typography variant="body1" align="center">
                    Select a pre-made template or click 'Create New' to start with an empty
                    template.
                </Typography>
            </div>
            <div className={classes.selectWrapper}>
                <SelectApTemplate
                    fullWidth={true}
                    error={Boolean(compState.formError?.field === 'templateId')}
                    helperText={
                        compState.formError?.field === 'templateId'
                            ? compState.formError.message
                            : ''
                    }
                    value={compState.templateId || ''}
                    onSelectHandler={compDispatch}
                    variant="outlined"
                />
            </div>
            <Grid
                container
                direction="row"
                wrap="nowrap"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                <Grid item xs="auto">
                    <ButtonLoading
                        isLoading={apCreateRequestStatus === RequestStatus.active}
                        onClick={handleCreate}
                    >
                        Create new
                    </ButtonLoading>
                </Grid>
                <Grid item xs="auto">
                    <ButtonLoading
                        isLoading={apSelectRequestStatus === RequestStatus.active}
                        onClick={handleSelect}
                        variant="outlined"
                        color="secondary"
                    >
                        Select Template
                    </ButtonLoading>
                </Grid>
            </Grid>
        </div>
    );
}
